// @file    customer-write.guard.ts
// @created 2026-06-25 by Claude
// @desc    Blocks the agent-only "create" routes (claims, cancellation, change
//          request) in the customer iHub app. iHub is view/track-only — the only
//          legitimate customer write-action is paying a premium. When the customer
//          build flag is active the guard redirects any *-create/:policyNo URL to
//          the matching *-list/:policyNo view, so a stale link / back-stack entry /
//          bookmark can't reach a submission form. Flip
//          `environment.customerCanSubmitRequests` to `true` for a future
//          agent / self-service build to allow the forms again (no code deleted).

import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { environment } from '../../../environments/environment';

/** Maps each guarded create route (base path) to its list view. */
const CREATE_TO_LIST: { [createBase: string]: string } = {
  'ih-claim-accident-create': 'ih-claim-accident-list',
  'ih-claim-dead-create': 'ih-claim-dead-list',
  'ih-policy-cancel-create': 'ih-policy-cancel-list',
  'ih-policy-change-request-create': 'ih-policy-change-request-list',
};

export const customerWriteGuard: CanActivateFn = (route): boolean | UrlTree => {
  // Agent/self-service build: let the create form open.
  if (environment.customerCanSubmitRequests) {
    return true;
  }

  const router = inject(Router);
  const policyNo = route.paramMap.get('policyNo');
  const createBase = (route.routeConfig?.path ?? '').split('/')[0];
  const listPath = CREATE_TO_LIST[createBase];

  if (listPath) {
    return router.createUrlTree(policyNo ? ['/' + listPath, policyNo] : ['/' + listPath]);
  }

  // Unknown create route — fall back to home rather than the form.
  return router.createUrlTree(['/ih-home']);
};
